import { action, makeObservable, observable, runInAction } from "mobx";
import SwagstorePurchaseTransactionAPI from "../../../api/SwagstorePurchaseTransactionAPI";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import {
  buildDefaultPurchaseTransactionQuery,
  PurchaseTransactionDownloadResult,
  PurchaseTransactionListQuery,
  UserPurchaseTransactionApiRow,
} from "../../../models/Swagstore/UserPurchaseTransactions";

export class SwagstorePurchaseTransactionStore {
  private readonly api = new SwagstorePurchaseTransactionAPI();
  private requestSeq = 0;

  storeName: string | null = null;
  rows: UserPurchaseTransactionApiRow[] = [];
  query: PurchaseTransactionListQuery;
  totalCount = 0;
  totalPages = 0;
  loading = false;
  downloadLoading = false;
  error: string | null = null;
  isFiltersExpanded = true;

  constructor(initialQuery: PurchaseTransactionListQuery) {
    this.query = initialQuery;

    makeObservable(this, {
      storeName: observable,
      rows: observable,
      query: observable.ref,
      totalCount: observable,
      totalPages: observable,
      loading: observable,
      downloadLoading: observable,
      error: observable,
      isFiltersExpanded: observable,
      setStoreName: action,
      setQuery: action,
      setIsFiltersExpanded: action,
      resetFilters: action,
      fetchTransactions: action,
      downloadTransactionsReport: action,
      clearError: action,
    });
  }

  setStoreName = (name: string | null) => {
    this.storeName = name;
  };

  setQuery = (patch: Partial<PurchaseTransactionListQuery>) => {
    const nextQuery = { ...this.query, ...patch };

    (Object.keys(patch) as Array<keyof PurchaseTransactionListQuery>).forEach(
      (key) => {
        if (patch[key] === undefined) {
          delete nextQuery[key];
        }
      },
    );

    this.query = nextQuery;
  };

  setIsFiltersExpanded = (expanded: boolean) => {
    this.isFiltersExpanded = expanded;
  };

  resetFilters = () => {
    this.query = buildDefaultPurchaseTransactionQuery(
      this.query.store_id,
      this.query.customer_id,
    );
  };

  clearError = () => {
    this.error = null;
  };

  fetchTransactions = async () => {
    const seq = ++this.requestSeq;
    const query = this.query;

    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const response = await this.api.getPurchaseTransactions(query);
      if (seq !== this.requestSeq) return;

      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch transactions");
      }

      const result =
        response.result as PaginatedAPIResponse<UserPurchaseTransactionApiRow>;
      runInAction(() => {
        this.rows = result?.list || [];
        this.totalCount = result?.total_count || 0;
        this.totalPages = result?.total_pages || 0;
        this.loading = false;
      });
    } catch (error: any) {
      if (seq !== this.requestSeq) return;
      runInAction(() => {
        this.rows = [];
        this.totalCount = 0;
        this.totalPages = 0;
        this.loading = false;
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch purchase transactions";
      });
    }
  };

  downloadTransactionsReport =
    async (): Promise<PurchaseTransactionDownloadResult> => {
      if (!this.query.start_date || !this.query.end_date) {
        throw new Error("Select a start and end date to download the report");
      }

      runInAction(() => {
        this.downloadLoading = true;
      });

      try {
        return await this.api.downloadPurchaseTransactions(this.query);
      } finally {
        runInAction(() => {
          this.downloadLoading = false;
        });
      }
    };
}
